import React, { useState, useMemo, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Pagination,
} from '@mui/material'
import { Assignment } from '@mui/icons-material'
import { useNavigation, useList } from '@refinedev/core'
import { ActivityFilterButtons } from './ActivityFilterButtons'
import { NoActivitiesMessage } from './NoActivitiesMessage'
import { ActivityType } from '../types/activity'
import {
  getActivityColor,
  getActivityIcon,
  formatActivityType,
  buildMembershipSequence,
  normalizeActivityItem,
  formatTimestampNZ,
} from '../utils/activityDisplay'
import {
  calculateTypeCounts,
  loadFilterPreferences,
  saveFilterPreferences,
} from '../utils/activityFilters'

// Import ActivityItem from types
import type { ActivityItem } from '../types/activity'

interface ActivityFeedAllProps {
  pageSize?: number
  showTitle?: boolean
}

// membership helpers moved to utils/activityDisplay

function sortActivitiesForSingleOrg(activities: ActivityItem[]) {
  return activities.sort((a, b) => {
    const aTime = new Date(a.date || a.activity_timestamp)
    const bTime = new Date(b.date || b.activity_timestamp)

    // apply membership sequence logic for same-date membership changes
    if (
      a.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
      b.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
      aTime.toDateString() === bTime.toDateString()
    ) {
      // get all membership changes for this date - this is all same org
      const sameDateMembershipChanges = activities.filter(
        (activity) =>
          activity.activity_type === ActivityType.MEMBERSHIP_CHANGE &&
          new Date(activity.date || activity.activity_timestamp).toDateString() === aTime.toDateString()
      )

      const orderedSequence = buildMembershipSequence(sameDateMembershipChanges)

      // find positions of a and b in the ordered sequence
      const aIndex = orderedSequence.findIndex(
        (item) => item.activity_details === a.activity_details
      )
      const bIndex = orderedSequence.findIndex(
        (item) => item.activity_details === b.activity_details
      )

      if (aIndex !== -1 && bIndex !== -1) {
        //  show most recent first
        return aIndex - bIndex
      }
    }

    // default sort by date (most recent first)
    return bTime.getTime() - aTime.getTime()
  })
}

function sortActivities(activities: ActivityItem[]) {
  // group activities by org_uuid
  const activitiesByOrg = activities.reduce((acc, activity) => {
    const orgUuid = activity.org_uuid || 'no-org'
    if (!acc[orgUuid]) {
      acc[orgUuid] = []
    }
    acc[orgUuid].push(activity)
    return acc
  }, {} as Record<string, ActivityItem[]>)

  // sort activities within each org using the membership sequence logic, this ensures that membership changes are displayed in the correct order for each org when date is unreliable
  const sortedByOrg = Object.entries(activitiesByOrg).map(
    ([orgUuid, orgActivities]) => ({
      orgUuid,
      activities: sortActivitiesForSingleOrg(orgActivities),
    })
  )

  // merge all sorted org activities back into chronological order while preserving the membership sequences within each org
  const allSortedActivities = sortedByOrg.flatMap((org) => org.activities)

  // final sort by timestamp, ensuring that activities from the same org/date are already in the correct relative order
  return allSortedActivities.sort((a, b) => {
    const aTime = new Date(a.date || a.activity_timestamp)
    const bTime = new Date(b.date || b.activity_timestamp)

    // if same org and same date, preserve the order we already established
    if (
      a.org_uuid === b.org_uuid &&
      aTime.toDateString() === bTime.toDateString()
    ) {
      // find their positions in the already-sorted org activities
      const orgActivities =
        sortedByOrg.find((org) => org.orgUuid === a.org_uuid)?.activities || []
      const aIndex = orgActivities.findIndex((activity) => activity === a)
      const bIndex = orgActivities.findIndex((activity) => activity === b)

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }
    }

    // default chronological sort (most recent first)
    return bTime.getTime() - aTime.getTime()
  })
}

const ActivityFeedAll: React.FC<ActivityFeedAllProps> = ({
  pageSize = 20,
  showTitle = true,
}) => {
  const { show } = useNavigation()
  const [currentPage, setCurrentPage] = React.useState(1)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Filter state
  const [selectedType, setSelectedType] = useState<ActivityType | 'all'>(() =>
    loadFilterPreferences()
  )

  const {
    data: activityData,
    isLoading,
    isError,
  } = useList({
    resource: 'org-activity',
    pagination: {
      current: currentPage,
      pageSize,
    },
    meta: {
      activityType: selectedType,
    },
  })

  // Use server-provided data and counts
  const activities = (activityData?.data || []).map(normalizeActivityItem) as ActivityItem[]

  // Use server-provided type counts or calculate from current data as fallback
  const typeCounts = useMemo(() => {
    if ((activityData as any)?.typeCounts) {
      return (activityData as any).typeCounts as Record<ActivityType, number>
    }
    // Fallback to calculating from current page (backward compatibility)
    return calculateTypeCounts(activities)
  }, [activityData, activities])

  // Total pages based on filtered results
  const totalPages = activityData?.total
    ? Math.ceil(activityData.total / pageSize)
    : 1

  // Handle filter changes
  const handleFilterChange = (type: ActivityType | 'all') => {
    setSelectedType(type)
    saveFilterPreferences(type)
    setCurrentPage(1) // Reset to first page when filters change
  }

  // Save preferences when they change
  useEffect(() => {
    saveFilterPreferences(selectedType)
  }, [selectedType])

  if (isLoading) {
    return <Typography>Loading activity information...</Typography>
  }

  if (isError) {
    return <Typography>Error loading activity information.</Typography>
  }

  // Function to reset filters
  const handleResetFilter = () => {
    setSelectedType('all')
    saveFilterPreferences('all')
  }

  if (activities.length === 0) {
    return (
      <Box ref={containerRef}>
        {showTitle && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            Recent Activity
          </Typography>
        )}
        <ActivityFilterButtons
          selectedType={selectedType}
          onFilterChange={handleFilterChange}
          typeCounts={typeCounts}
        />
        <NoActivitiesMessage
          selectedType={selectedType}
          onReset={handleResetFilter}
        />
      </Box>
    )
  }

  // SPECIAL CASE: org_created activities with date 2025-05-20 should be the first activity, this is a special case for when orgs created_at field was added
  const orgCreatedActivities = activities.filter(
    (activity) =>
      activity.activity_type === 'org_created' &&
      (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
  )

  const otherActivities = activities.filter(
    (activity) =>
      !(
        activity.activity_type === 'org_created' &&
        (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
      )
  )

  // custom sort other activities for membership changes
  const sortedOtherActivities = sortActivities(otherActivities)

  // combine other activities first, then special case (org created) activities at bottom
  const sortedActivities = [...sortedOtherActivities, ...orgCreatedActivities]

  // for rendering purposes, to not show date as '2025-05-20'
  const isSpecialCase = (activity: ActivityItem) => {
    return (
      activity.activity_type === 'org_created' &&
      (activity.date || activity.activity_timestamp).startsWith('2025-05-20')
    )
  }

  function handleActivityClick(activity: ActivityItem) {
    if (!activity.org_uuid) return

    show('orgs', activity.org_uuid)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of the activity list
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Box ref={containerRef}>
      {showTitle && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Recent Activity
        </Typography>
      )}
      <ActivityFilterButtons
        selectedType={selectedType}
        onFilterChange={handleFilterChange}
        typeCounts={typeCounts}
      />
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => handlePageChange(page)}
            color="primary"
            size="large"
          />
        </Box>
      )}
      {sortedActivities.map((activity, index) => (
        <Card
          key={`${activity.org_uuid || 'no-org'}-${activity.date || activity.activity_timestamp}-${index}`}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              color="primary"
              onClick={() => handleActivityClick(activity)}
              sx={{ cursor: 'pointer' }}
            >
              {activity.org_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar
                sx={{
                  bgcolor: getActivityColor(activity.activity_type),
                  width: 32,
                  height: 32,
                  mr: 2,
                  cursor: 'pointer',
                }}
              >
                {getActivityIcon(activity.activity_type)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  {formatActivityType(activity.activity_type)}
                </Typography>
                {!isSpecialCase(activity) && (
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestampNZ(activity.date || activity.activity_timestamp)}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body2">{activity.activity_details}</Typography>
          </CardContent>
        </Card>
      ))}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => handlePageChange(page)}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  )
}

export default ActivityFeedAll
